// src/App.jsx
import './App.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import StaffingDashboard from './pages/StaffingDashboard';
import ClinicianApply from './pages/ClinicianApply';

// --- Components ---
import ProRoute from './components/ProRoute';

// --- Pages ---
import OrganizationLanding from './pages/OrganizationLanding';
import LumoChartLanding from './pages/LumoChartLanding';
import LumoDxLanding from './pages/LumoDxLanding';
import MedicalStaffingLanding from './pages/MedicalStaffingLanding';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AddPatient from './pages/AddPatient';
import EditProfile from './pages/Profile';
import PatientDetailRouter from './pages/PatientDetailRouter';
import InpatientDetail from './pages/InpatientDetail';
import ProgressNote from './pages/ProgressNote';
import EDDetail from './pages/EDDetail';
import OutpatientDetail from './pages/OutpatientDetail';
import ConsultDetail from './pages/ConsultDetail';
import PastNotes from './pages/PastNotes';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import EditPatient from './pages/EditPatient';
import AdmissionNote from './pages/AdmissionNote';
import HospitalCourse from './pages/HospitalCourse';
import DischargeSummary from './pages/DischargeSummary';
import NewVisitNote from './pages/NewVisitNote';
import FollowUpVisitNote from './pages/FollowUpVisitNote';
import EDNote from './pages/EDNote';
import DispositionTools from './pages/DispositionTools';
import InitialConsultNote from './pages/InitialConsultNote';
import FollowUpConsultNote from './pages/FollowUpConsultNote';
import SupportPage from './pages/SupportPage';
import Upgrade from './pages/Upgrade';

// --- Private Route Wrapper ---
function PrivateRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  return currentUser ? children : <Navigate to="/login" />;
}

// --- App Router ---
export default function App() {
  return (
    <AuthProvider>
      <UserProfileProvider>
        <ThemeProvider>
          <SubscriptionProvider>
            <Router>
              <Routes>
                {/* 🔓 Public Routes */}
                <Route path="/" element={<OrganizationLanding />} />
                <Route path="/lumochart" element={<LumoChartLanding />} />
                <Route path="/lumodx" element={<LumoDxLanding />} />
                <Route path="/medical-staffing" element={<MedicalStaffingLanding />} />
                <Route path="/staffing-dashboard" element={<StaffingDashboard />} />
                <Route path="/apply" element={<ClinicianApply />} />
                <Route path="/login" element={<Login />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/upgrade" element={<Upgrade />} />

                {/* 🔐 Private Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/patients/new"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <AddPatient />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <EditProfile />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/patient/:id/detail"
                  element={
                    <PrivateRoute>
                      <PatientDetailRouter />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/patients/:id/notes"
                  element={
                    <PrivateRoute>
                      <PastNotes />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/edit-patient/:id"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <EditPatient />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                {/* Clinical Setting Views */}
                <Route
                  path="/inpatient-detail/:id"
                  element={
                    <PrivateRoute>
                      <InpatientDetail />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/ed-detail/:id"
                  element={
                    <PrivateRoute>
                      <EDDetail />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/outpatient-detail/:id"
                  element={
                    <PrivateRoute>
                      <OutpatientDetail />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/consult-detail/:id"
                  element={
                    <PrivateRoute>
                      <ConsultDetail />
                    </PrivateRoute>
                  }
                />

                {/* ---- NOTE TYPES (PRO ONLY) ---- */}
                <Route
                  path="/admission-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <AdmissionNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/progress-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <ProgressNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/hospital-course/:patientId"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <HospitalCourse />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/discharge-summary/:patientId"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <DischargeSummary />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/new-visit-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <NewVisitNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/follow-up-visit-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <FollowUpVisitNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/ed-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <EDNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/disposition-tools/:patientId"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <DispositionTools />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/initial-consult-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <InitialConsultNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/follow-up-consult-note/:patientId/:noteId?"
                  element={
                    <PrivateRoute>
                      <ProRoute>
                        <FollowUpConsultNote />
                      </ProRoute>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Router>
          </SubscriptionProvider>
        </ThemeProvider>
      </UserProfileProvider>
    </AuthProvider>
  );
}